import React, { useState } from 'react';
import PropTypes from 'prop-types';
import chevronGreyIcon from 'assets/images/icons/chevron-grey.svg';
import boundIcon from 'assets/images/icons/bound.svg';
import unBoundIcon from 'assets/images/icons/un-bound.svg';
import doneIcon from 'assets/images/icons/done-circle-green-filled.svg';
import MultiLevelSelect from 'components/form-components/MultiLevelSelect';
import {
  dashboardFontSizeOptions,
  dashboardFontWeightOptions,
  dashboardFormattingDisplayOptionsList,
} from '../utils';
import { useTranslation } from 'react-i18next';
import ValueRanges from './ValueRanges';

const ValueFormatting = ({ values, handleChange }) => {
  const { t } = useTranslation();
  const [showContent, setShowContent] = useState(true);

  return (
    <>
      <div className='text-and-link hyper-link accordion d-flex flex-column'>
        <div className='accordion-item border-0 px-3 py-2 mb-0'>
          <div className='d-flex justify-content-between align-items-center'>
            <span className='theme-text-black-1 theme-size-1_1 theme-font-inter-medium'>
              {t('text_value_formatting')}
            </span>
            <img
              src={chevronGreyIcon}
              className={`toggle-button cursor-pointer p-2 ${showContent ? '' : 'rotate-180'}`}
              onClick={() => setShowContent((prevState) => !prevState)}
            />
          </div>
        </div>

        <div className='accordion-item border-0 theme-text-grey-1'>
          {showContent && (
            <div className='p-3 border-top-grey-10-h-1 d-flex flex-column gap-3'>
              {'formatting' in values && (
                <div className='d-flex flex-wrap'>
                  <div className='col-4'>{t('text_format')}:</div>
                  <div className='col-8 d-flex gap-3'>
                    <div
                      className={`w-50 format-box ${
                        values.formatting === 'conditional' ? '' : 'active'
                      }`}
                      onClick={() => handleChange('formatting', 'default')}
                    >
                      <span>{t('text_default')}</span>
                      <img className='d-block mx-auto mt-2' src={boundIcon} />
                      {values.formatting === 'default' && (
                        <img
                          className='theme-width-20 m-2 position-absolute end-0 top-0'
                          src={doneIcon}
                        />
                      )}
                    </div>
                    <div
                      className={`w-50 format-box ${
                        values.formatting === 'conditional' ? 'active' : ''
                      }`}
                      onClick={() => handleChange('formatting', 'conditional')}
                    >
                      <span>{t('text_conditional')}</span>
                      <img className='d-block mx-auto mt-2' src={unBoundIcon} />
                      {values.formatting === 'conditional' && (
                        <img
                          className='theme-width-20 m-2 position-absolute end-0 top-0'
                          src={doneIcon}
                        />
                      )}
                    </div>
                  </div>
                </div>
              )}

              {'formattingDisplayOption' in values && (
                <div className='d-flex flex-wrap align-items-center'>
                  <div className='col-4'>{t('text_show_as')}:</div>
                  <div className='col-8'>
                    <div className='w-100'>
                      <MultiLevelSelect
                        options={dashboardFormattingDisplayOptionsList}
                        value={[]}
                        name='formattingDisplayOption'
                        handleChange={(name, value) => handleChange(name, value[0])}
                        classes='theme-font-inter-regular'
                        toggleClasses='px-2 py-2 d-flex gap-2 justify-content-between align-items-center rounded-sm cursor-pointer border-grey-1-h-1'
                        toggleComponent={
                          <>
                            <label className='cursor-pointer'>
                              {t(
                                dashboardFormattingDisplayOptionsList.children.find(
                                  (o) => o.value === values.formattingDisplayOption,
                                )?.displayValue || 'select_select',
                              )}
                            </label>
                            <img className='rotate-180' src={chevronGreyIcon} />
                          </>
                        }
                        fullWidth
                      />
                    </div>
                  </div>
                </div>
              )}

              <div className='d-flex flex-wrap'>
                <div className='col-4'>{t('text_value')}:</div>
                <div className='col-8 d-flex gap-3'>
                  <div className='w-100'>
                    <label>{t('field_font_size')}</label>
                    <MultiLevelSelect
                      options={dashboardFontSizeOptions}
                      value={[]}
                      name='formattingFontSize'
                      handleChange={(name, value) => handleChange(name, value[0])}
                      classes='theme-font-inter-regular'
                      toggleClasses='px-2 py-2 d-flex gap-2 justify-content-between align-items-center rounded-sm cursor-pointer border-grey-1-h-1'
                      toggleComponent={
                        <>
                          <label className='cursor-pointer'>
                            {dashboardFontSizeOptions.children.find(
                              (o) => o.value === values.formattingFontSize,
                            )?.displayValue || t('select_select')}
                          </label>
                          <img className='rotate-180' src={chevronGreyIcon} />
                        </>
                      }
                      fullWidth
                    />
                  </div>
                  <div className='w-100'>
                    <label>{t('field_font_type')}</label>
                    <MultiLevelSelect
                      options={dashboardFontWeightOptions}
                      value={[]}
                      name='formattingFontWeight'
                      handleChange={(name, value) => handleChange(name, value[0])}
                      classes='theme-font-inter-regular'
                      toggleClasses='px-2 py-2 d-flex gap-2 justify-content-between align-items-center rounded-sm cursor-pointer border-grey-1-h-1'
                      toggleComponent={
                        <>
                          <label className='cursor-pointer'>
                            {t(
                              dashboardFontWeightOptions.children.find(
                                (o) => o.value === values.formattingFontWeight,
                              )?.displayValue || 'select_select',
                            )}
                          </label>
                          <img className='rotate-180' src={chevronGreyIcon} />
                        </>
                      }
                      fullWidth
                    />
                  </div>
                </div>
              </div>

              {'formattingRanges' in values && values.formatting === 'conditional' && (
                <div className='d-flex flex-wrap'>
                  <div className='col-4'>{t('text_value_ranges')}:</div>
                  <div className='col-8 d-flex flex-column gap-2'>
                    <ValueRanges
                      name='formattingRanges'
                      value={values.formattingRanges}
                      onChange={handleChange}
                    />
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

ValueFormatting.propTypes = {
  values: PropTypes.object.isRequired,
  handleChange: PropTypes.func.isRequired,
};

export default ValueFormatting;
